<script setup lang="ts">
/**
 * A field's validation message.
 *
 * ## `reserve`, and why a form should use it
 *
 * By default this is `v-show`n, which is `display: none` — the message takes no
 * space until it appears, so the moment it does every field below it jumps down
 * by a line. In a multi-column grid it is worse: one cell growing makes the
 * whole ROW taller, so a message under "Client email" shoves the entire next row
 * down (measured at 40px on the quote form).
 *
 * That jump lands exactly when the user is reading, and it moves the thing they
 * were about to click. `reserve` keeps the line whether or not there is a
 * message, so the form is the same height before and after validation.
 *
 * It reserves ONE line. A message long enough to wrap still moves things — keep
 * validation copy short, which it should be anyway.
 *
 * The default slot follows the message on the same line, for a short action
 * such as a link to the record the message is about.
 *
 * The message is `role="alert"`, so a screen reader announces it the moment it
 * appears; `aria-invalid` on the field alone says nothing until the user
 * returns to it. Pass `id` and point the field's `aria-describedby` at it so the
 * message is also read with the field.
 */
defineProps<{
    /** Null and undefined both mean "no error" — dialogs holding one message usually keep it as `string | null`. */
    message?: string | null;
    /** Hold the line open so an appearing message cannot reflow the form. */
    reserve?: boolean;
    /** Id for the message, for the field's `aria-describedby`. */
    id?: string;
}>();
</script>

<template>
    <div v-show="reserve || message" data-validation-error :class="reserve && 'min-h-5'">
        <p v-if="message" :id="id" role="alert" class="text-destructive dark:text-destructive text-sm">
            {{ message }}
            <slot />
        </p>
    </div>
</template>
